const API_URL = process.env.REACT_APP_API_URL || 'https://api.playkokki.com/';
const BASE_URL = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;

export interface News {
    id: number;
    title: string;
    description: string | null;
    imageUrl: string | null;
    buttonText: string | null;
    buttonColor: string | null;
    navigationUrl: string | null;
    position: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreateNewsDto {
    title: string;
    description?: string;
    imageUrl?: string;
    buttonText?: string;
    buttonColor?: string;
    navigationUrl?: string;
    position: number;
    isActive?: boolean;
}

export interface UpdateNewsDto {
    title?: string;
    description?: string;
    imageUrl?: string;
    buttonText?: string;
    buttonColor?: string;
    navigationUrl?: string;
    position?: number;
    isActive?: boolean;
}

// Get JWT token from Kokki main app (stored when user logs in)
const getAuthToken = (): string | null => {
    return localStorage.getItem('kokki-jwt-token');
};

// Set JWT token (admin can set this manually or from main app)
export const setAuthToken = (token: string): void => {
    localStorage.setItem('kokki-jwt-token', token);
};

// Get auth headers
const getAuthHeaders = (): HeadersInit => {
    const token = getAuthToken();
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

// Fetch all news
export const fetchAllNews = async (includeInactive: boolean = true): Promise<News[]> => {
    const response = await fetch(
        `${BASE_URL}news?includeInactive=${includeInactive}`,
        { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Failed to fetch news');
    return response.json();
};

// Fetch single news
export const fetchNewsById = async (id: number): Promise<News> => {
    const response = await fetch(`${BASE_URL}news/${id}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch news');
    return response.json();
};

// Create news
export const createNews = async (data: CreateNewsDto): Promise<News> => {
    const response = await fetch(`${BASE_URL}news`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create news' }));
        throw new Error(error.message || 'Failed to create news');
    }
    return response.json();
};

// Update news
export const updateNews = async (id: number, data: UpdateNewsDto): Promise<News> => {
    const response = await fetch(`${BASE_URL}news/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update news' }));
        throw new Error(error.message || 'Failed to update news');
    }
    return response.json();
};

// Delete news
export const deleteNews = async (id: number): Promise<News> => {
    const response = await fetch(`${BASE_URL}news/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete news' }));
        throw new Error(error.message || 'Failed to delete news');
    }
    return response.json();
};
